// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Assessor/Collector upload template → parsed rows. PURE (no Supabase), so it runs in Node against the real
// state files (scripts/check-assessor-templates.mjs) as well as in ImportTemplateModalTwoTrack.
//
// ⛔ READ BY HEADER, NOT BY POSITION. The state files are not one layout. Measured across all 14 on 2026-10-06:
//   • Collectors identity: 7 files put Value Year BEFORE Assessor (AL AZ GA CO MA MN NJ), the rest after.
//   • Installment blocks: the BPP section starts at column 25, 32, 38 or 50, with 1–4 blocks per section, and
//     newer files have no spacer between blocks. A fixed start + stride read garbage on HI and MO.
//   • Installment number: older files say "Installment 1"/"Annual Tax Bill"; HI/MO/MN put a bare 1 or 2 in an
//     "Installment" column — which the old text-only check skipped WITHOUT ERROR (zero installments imported).
// Both tabs have a two-row header: row 0 = group band (General / Real Estate / …), row 1 = column headers;
// data starts at row 2. Every field is found by (band, header name) — a duplicate name like "State" is
// disambiguated by its band, or by occurrence within it (assessor State vs mailing State).
//
// ⛔ EXCEPT INSIDE AN INSTALLMENT BLOCK. A block is found by its header ("Bill Type" or "Installment") and
// must be six columns ending in "Due", but its values are read in the FIXED order
// [type, ratio, % of bill, adjustment type, adjustment factor, due]. From block 2 on, the HI and MN headers are
// mislabelled — "Adjustment" sits over the 0.5 that is really the installment % — while the VALUES follow
// block 1's order. Reading those cells by name would put 50% into the adjustment and default the split to 100%.
// ═══════════════════════════════════════════════════════════════════════════════════════════════
import * as XLSX from 'xlsx';
import { MONTHS } from '../constants/months';
import { toStateCode } from '../constants/usStates';
import { entityKey } from './entityKey';
import { EXEMPT_TEXT, BPP_EXEMPT_FIELDS, BPP_OTHER_FIELDS } from './assessorExempt';
import { composeCompliance, composeSeasonMY, parseMonthDay, parseMonthYear, parseComplianceMY, composeComplianceMY } from '../constants/refFields';

const VALID_BILL_TYPES = new Set([
  'annual tax bill', 'installment 1', 'installment 2', 'installment 3', 'installment 4', 'supplemental', 'delinquent',
]);
const BLOCK_STARTS = new Set(['bill type', 'installment']);

const norm = (s) => String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim();
const txt = (v) => { const s = v == null ? '' : String(v).trim(); return s || null; };
const numOrNull = (v) => { if (v == null || String(v).trim() === '') return null; const cleaned = String(v).replace(/[^0-9.-]/g, ''); if (cleaned === '') return null; const n = Number(cleaned); return isNaN(n) ? null : n; };  // non-numeric text (Exempt/N/A/typo) → null, not 0
const nkey = (s) => entityKey(s);

// ── DTAG load fixes (Philip, 2026-10-11 — docs/dtag-assessors-dryrun-report.md) ──
// ZIP: a ZIP cell typed as a number loses its leading zero (Massachusetts 02351 arrived as 2351 on all 670 rows).
// Fewer than five digits is never a real ZIP, so it is padded back; ZIP+4 pads its first part. Anything else as-is.
const zip = (v) => {
  const s = txt(v); if (!s) return null;
  const m = /^(\d{1,5})(?:\.0+)?(-\d{4})?$/.exec(s);
  return m ? m[1].padStart(5, '0') + (m[2] || '') : s;
};
// RATIO: Wigley stores a ratio as a decimal (0.4). Georgia's Collectors tab writes "40%" as TEXT, which numOrNull
// read as 40 — 100× — and the collector form showed 4000%. A value written with % is a percent; anything else is
// already the decimal the template's numeric cells hold (0.4, 0.26, 1).
const ratioOf = (v) => {
  const n = numOrNull(v);
  return n != null && /%/.test(String(v)) ? Math.round(n * 10000) / 1000000 : n;
};
// States with no tax on business personal property: the PP track imports as EXEMPT — the shape Add/Edit Assessor
// writes when its box is ticked (utils/assessorExempt) and pp_exempt on the collector — never as "N/A" text that the
// form can only show as "previously: N/A". Hawaii and Ohio (Philip, 2026-10-11).
export const BPP_EXEMPT_STATES = new Set(['HI', 'OH']);

function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 1) return 2;
  const dp = Array.from({ length: m + 1 }, (_, i) => i);
  for (let j = 1; j <= n; j++) {
    let prev = dp[0]; dp[0] = j;
    for (let i = 1; i <= m; i++) { const tmp = dp[i]; dp[i] = Math.min(dp[i] + 1, dp[i - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1)); prev = tmp; }
  }
  return dp[m];
}
function resolveMonth(name) {
  const n = norm(name); if (!n) return null;
  const exact = MONTHS.find(x => norm(x.label) === n); if (exact) return exact.value;
  if (n.length < 5) return null;
  const near = MONTHS.filter(x => levenshtein(norm(x.label), n) <= 1);
  return near.length === 1 ? near[0].value : null;
}
// ═══ EXCEL DATE CELLS (Philip 2026-10-30) ═══ Rows are read raw (sheetRows), so a date-typed cell arrives as its serial
// number: AL's Assessment Date — 2026-10-01 formatted "mmmm d", shown as "October 1" — was stored as the text "46296".
// Every date-capable text field goes through dateText: a serial becomes the field's own words, month-day ("October 1")
// or month-year ("December 2025"); anything else passes through unchanged. (Due dates use toISODate, which already did.)
const serialDate = (v) => (typeof v === 'number' && v >= 20000 && v < 80000 ? XLSX.SSF.parse_date_code(v) : null);
const dateText = (v, withYear) => {
  const d = serialDate(v);
  return d ? (withYear ? composeSeasonMY(d.m, d.y) : composeCompliance(d.m, d.d, false, '')) : v;
};
// Sheet date → ISO 'YYYY-MM-DD'. Prefers an explicit year in the cell (fiscal-year 2nd installments),
// else falls back to yearHint (the row's value year). Unresolvable → null (caller decides drop vs skip).
function toISODate(v, yearHint) {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && XLSX.SSF) { const d = XLSX.SSF.parse_date_code(v); if (d) return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`; }
  const s = String(v).trim();
  const md = s.match(/^([A-Za-z]+)\s+(\d{1,2})(?:\s*,?\s*(\d{4}))?/);
  if (md) { const mv = resolveMonth(md[1]); const yr = md[3] || yearHint; return (mv && yr) ? `${yr}-${String(mv).padStart(2, '0')}-${String(parseInt(md[2], 10)).padStart(2, '0')}` : null; }
  const dt = new Date(s); if (!isNaN(dt)) return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  return null;
}
// % Tax Bill → split_pct as PERCENT (rule 8: 0.5→50, 1→100). ≤1 → ×100; >1 → as-is; blank → 100.
function toSplitPct(v) {
  if (v == null || String(v).trim() === '') return 100;
  const n = Number(String(v).replace(/[^0-9.-]/g, '')); if (isNaN(n)) return 100;
  return n <= 1 ? Math.round(n * 100) : Math.round(n);
}
// installment_number derived from Bill Type (⚠️ verify): "Installment N"→N; "Annual Tax Bill"→1; else null.
function billTypeToInstNum(bt) { const m = /installment\s*(\d)/i.exec(bt || ''); if (m) return Number(m[1]); if (/annual/i.test(bt || '')) return 1; return null; }
// tax_type (rule 5): default 'Value Based'; "Last years total tax amount" (Cook inst-1) → 'Non-Value Based'.
const taxTypeFor = (adjType) => norm(adjType) === 'last years total tax amount' ? 'Non-Value Based' : 'Value Based';
// A block's type cell: the text bill types, or a bare installment number 1–4 (HI/MO/MN) → "Installment N".
function billTypeOf(cell) {
  const s = txt(cell);
  if (s && /^[1-4](\.0+)?$/.test(s)) return `Installment ${Number(s)}`;
  return s && VALID_BILL_TYPES.has(norm(s)) ? s : null;
}

// Parse one track's blocks. RULE 3 GUARD: create an installment ONLY where the block's type cell is valid —
// skips date/blank/"N/A"/other (FL drag-fill corrupts blocks 2-4). Values in the fixed in-block order (see top).
function parseBlocks(row, starts, kind, valueYear) {
  const out = [], drops = [];
  for (const base of starts) {
    const billType = billTypeOf(row[base]);
    if (!billType) continue;                                              // rule 3 — not a valid bill type → not a real block
    const due = row[base + 5];
    const dueISO = toISODate(due, valueYear);
    if (!dueISO && txt(due)) { drops.push({ kind, bill_type: billType, raw: String(due).trim() }); continue; }   // valid type but garbled non-empty due → loud drop
    const adjType = txt(row[base + 3]);
    out.push({
      kind, bill_type: billType, installment_number: billTypeToInstNum(billType),
      ratio: ratioOf(row[base + 1]),
      split_pct: toSplitPct(row[base + 2]),
      adjustment_type: adjType,
      adjustment_pct: numOrNull(row[base + 4]),
      tax_type: taxTypeFor(adjType),
      due_date: dueISO,
    });
  }
  return { out, drops };
}

// ── layout from the two header rows ─────────────────────────────────────────────────────────────
// Bands: each named cell on row 0 owns the columns up to the next named cell.
function bandsOf(bandRow, width) {
  const starts = [];
  for (let i = 0; i < width; i++) if (txt(bandRow[i])) starts.push(i);
  return starts.map((s, k) => ({ name: norm(bandRow[s]), start: s, end: (k + 1 < starts.length ? starts[k + 1] : width) - 1 }));
}
function layoutReader(tab, bandRow, hdrRow) {
  const width = Math.max(bandRow.length, hdrRow.length);
  const bands = bandsOf(bandRow, width);
  const missing = [];
  const band = (name, after = -1) => bands.find(b => b.name === norm(name) && b.start > after);
  // The nth (0-based) column in `bandName` whose header is any of `names`.
  const col = (bandName, names, nth = 0) => {
    const b = band(bandName);
    const want = [].concat(names).map(norm);
    if (b) {
      let seen = 0;
      for (let i = b.start; i <= b.end; i++) if (want.includes(norm(hdrRow[i])) && seen++ === nth) return i;
    }
    missing.push(`"${[].concat(names).join('" / "')}"${nth ? ` (#${nth + 1})` : ''} under "${bandName}"`);
    return -1;
  };
  // `after`: the first band of that name starting right of that column. `optional`: no such band = no blocks, not a refusal.
  const blocks = (bandName, { after = -1, optional = false } = {}) => {
    const b = band(bandName, after);
    if (!b) { if (!optional) missing.push(`band "${bandName}"`); return []; }
    const starts = [];
    for (let i = b.start; i <= b.end; i++) if (BLOCK_STARTS.has(norm(hdrRow[i]))) starts.push(i);
    starts.forEach((s, k) => {
      const next = k + 1 < starts.length ? starts[k + 1] : b.end + 1;
      if (next - s < 6 || norm(hdrRow[s + 5]) !== 'due') {
        missing.push(`a six-column block ending in "Due" at column ${s + 1} under "${bandName}" (found "${hdrRow[s + 5] ?? ''}")`);
      }
    });
    if (!starts.length) missing.push(`installment blocks ("Bill Type" / "Installment") under "${bandName}"`);
    return starts;
  };
  const done = (layout) => {
    if (missing.length) throw new Error(`Tab "${tab}": can't find ${missing.join('; ')}. Nothing was imported.`);
    return layout;
  };
  return { band, col, blocks, done };
}

function assessorLayout(bandRow, hdrRow) {
  const { col, done } = layoutReader('Assessors Screen', bandRow, hdrRow);
  const G = 'General', RE = 'Real Estate', PP = 'Business Personal Property';
  const track = (b) => ({
    assessment_date: col(b, 'Assessment Date'), reappraisal_year: col(b, 'Reappraisal Year'),
    reappraisal_cycle: col(b, 'Reappraisal Cycle'), fiscal_year: col(b, 'Fiscal Year'), ratio: col(b, 'Ratio'),
    compliance_season: col(b, 'Compliance Season'), extensions: col(b, 'Extensions (Y/N)'),
    notice_season: col(b, 'Notice Season'), appeal_season: col(b, ['Appeal Season', 'Appeal Deadline']),
  });
  return done({
    state: col(G, 'State', 0), name: col(G, 'Assessor'), value_year: col(G, 'Value Year'), url: col(G, 'URL'),
    maddr1: col(G, 'Mailing Address 1'), maddr2: col(G, 'Mailing Address 2'), mcity: col(G, 'City'),
    mstate: col(G, 'State', 1), mzip: col(G, 'Zip Code'),
    re: track(RE), pp: track(PP),
  });
}

function collectorLayout(bandRow, hdrRow) {
  const { band, col, blocks, done } = layoutReader('Collectors', bandRow, hdrRow);
  const C = 'Collector';
  // ⛔ THE BPP BILLING BAND (Philip 2026-10-25): Natalie's sheets also head it plain "Business Personal Property" (AR, CT) —
  // the band of that name to the right of Real Estate Tax Bill Processing, not the BPP fields band before it — or leave it
  // out altogether (IL): no BPP billing band = no BPP section, PP installments none, not a refusal.
  const reBilling = band('Real Estate Tax Bill Processing');
  const bppBlocks = band('Business Personal Property Tax Bill Processing')
    ? blocks('Business Personal Property Tax Bill Processing')
    : blocks('Business Personal Property', { after: reBilling ? reBilling.start : Infinity, optional: true });
  return done({
    join_state: col('Assessor', 'State'), join_assessor: col('Assessor', 'Assessor'), join_value_year: col('Assessor', 'Value Year'),
    name: col(C, 'Collector'), url: col(C, 'URL'), maddr1: col(C, 'Mailing Address 1'), maddr2: col(C, 'Mailing Address 2'),
    mcity: col(C, 'City'), mstate: col(C, 'State'), mzip: col(C, 'Zip Code'),
    re_tax_bill_season: col('Real Estate', 'Tax Bills Season'), re_fiscal_year: col('Real Estate', 'Fiscal Year'), re_ratio: col('Real Estate', 'Ratio'),
    bpp_tax_bill_season: col('Business Personal Property', 'Tax Bills Season'), bpp_fiscal_year: col('Business Personal Property', 'Fiscal Year'), bpp_ratio: col('Business Personal Property', 'Ratio'),
    re_blocks: blocks('Real Estate Tax Bill Processing'),
    bpp_blocks: bppBlocks,
  });
}

function findSheet(wb, want) { const n = wb.SheetNames.find(s => norm(s) === norm(want)); return n ? wb.Sheets[n] : null; }
// Every row as an array indexed from column A / row 1. Anchored at A1 explicitly: sheet_to_json numbers from the
// sheet's used range, and a tab whose column A is empty (Collectors) can have a range starting at B — shifting
// every index by one.
function sheetRows(ws) {
  const r = XLSX.utils.decode_range(ws['!ref'] || 'A1:A1');
  return XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: true, defval: null, range: { s: { r: 0, c: 0 }, e: r.e } });
}

// Workbook → { aByKey, parsed, failures }. Throws (nothing imported) when a tab or a required column is missing.
export function parseAssessorTemplate(wb) {
  const wsA = findSheet(wb, 'Assessors Screen'), wsC = findSheet(wb, 'Collectors');
  if (!wsA) throw new Error('Tab "Assessors Screen" not found');
  if (!wsC) throw new Error('Tab "Collectors" not found');
  const allA = sheetRows(wsA), allC = sheetRows(wsC);
  const AL = assessorLayout(allA[0] || [], allA[1] || []);
  const CL = collectorLayout(allC[0] || [], allC[1] || []);

  // 1) Assessors Screen → map (state|core) → two-track assessor fields.
  const aByKey = new Map();
  for (const row of allA.slice(2)) {
    const name = txt(row[AL.name]); const state = toStateCode(txt(row[AL.state]));
    if (!name || !state) continue;                    // blank pad row / unresolved state → skip silently
    const reRatio = ratioOf(row[AL.re.ratio]);   // a decimal; non-numeric text (e.g. "Varies") is kept as written below
    const valueYear = numOrNull(row[AL.value_year]);
    // Same stored formats as Add/Edit Assessor (constants/refFields, 2026-10-09): a value the template's pickers can
    // hold is written in their format; anything else is written exactly as the sheet has it, and the form shows it
    // as "previously: …" — an import never drops what the sheet says.
    const md = (v) => { const p = parseMonthDay(txt(dateText(v, false))); return p.month ? composeCompliance(p.month, p.day, false, '') : (p.legacy || null); };
    const my = (v) => { const p = parseMonthYear(txt(dateText(v, true))); return p.month ? composeSeasonMY(p.month, p.year) : (p.legacy || null); };
    const cmp = (v) => { const p = parseComplianceMY(txt(dateText(v, true)), valueYear); return composeComplianceMY(p.month, p.year, p.na, p.other); };
    const trackFields = (prefix, t) => ({
      [`${prefix}_assessment_date`]: md(row[t.assessment_date]), [`${prefix}_reappraisal_year`]: numOrNull(row[t.reappraisal_year]),
      [`${prefix}_reappraisal_cycle`]: txt(row[t.reappraisal_cycle]), [`${prefix}_fiscal_year`]: txt(row[t.fiscal_year]), [`${prefix}_ratio`]: ratioOf(row[t.ratio]),
      [`${prefix}_compliance_season`]: cmp(row[t.compliance_season]), [`${prefix}_extensions`]: txt(row[t.extensions]),
      [`${prefix}_notice_season`]: my(row[t.notice_season]), [`${prefix}_appeal_season`]: my(row[t.appeal_season]),
    });
    const assessor = {
      name, state, value_year: valueYear,
      mailing_address_1: txt(row[AL.maddr1]), mailing_address_2: txt(row[AL.maddr2]),
      mailing_city: txt(row[AL.mcity]), mailing_state: txt(row[AL.mstate]), mailing_zip: zip(row[AL.mzip]), url: txt(row[AL.url]),
      assessment_ratio: reRatio == null ? txt(row[AL.re.ratio]) : String(reRatio),   // shared reader col (ParcelModal:333) seeded from RE ratio (migration flag 1)
      ...trackFields('re', AL.re), ...trackFields('pp', AL.pp),
    };
    if (BPP_EXEMPT_STATES.has(state)) {
      BPP_EXEMPT_FIELDS.forEach((k) => { assessor[k] = EXEMPT_TEXT; });
      BPP_OTHER_FIELDS.forEach((k) => { assessor[k] = null; });
    }
    aByKey.set(`${state}|${nkey(name)}`, assessor);
  }

  // 2) Collectors tab → one entry per (assessor, collector) pairing, joined to the assessor above.
  const failures = [], parsed = [];
  allC.slice(2).forEach((row, i) => {
    const rowNo = i + 3;                                // 1-based sheet row: two header rows above the data
    const assessorName = txt(row[CL.join_assessor]); const collectorName = txt(row[CL.name]);
    if (!assessorName && !collectorName) return;                                     // blank pad row
    const state = toStateCode(txt(row[CL.join_state]));
    if (!state) { failures.push({ rowNo, reason: `unrecognized/blank state (col ${CL.join_state + 1})` }); return; }
    if (!assessorName) { failures.push({ rowNo, reason: `missing assessor name (col ${CL.join_assessor + 1})` }); return; }
    if (!collectorName) { failures.push({ rowNo, reason: `missing collector name (col ${CL.name + 1})` }); return; }
    const assessorFields = aByKey.get(`${state}|${nkey(assessorName)}`);
    if (!assessorFields) { failures.push({ rowNo, reason: `collector "${collectorName}": no matching assessor "${assessorName}" (${state}) on the Assessors Screen tab` }); return; }
    const valueYear = numOrNull(row[CL.join_value_year]) ?? assessorFields.value_year;

    const exempt = norm(row[CL.bpp_tax_bill_season]) === 'exempt' || BPP_EXEMPT_STATES.has(state);   // rule 4: BPP season = "Exempt" (or a no-BPP state)
    const collectorFields = {
      name: collectorName, state, url: txt(row[CL.url]),
      mailing_address_1: txt(row[CL.maddr1]), mailing_address_2: txt(row[CL.maddr2]),
      mailing_city: txt(row[CL.mcity]), mailing_state: txt(row[CL.mstate]), mailing_zip: zip(row[CL.mzip]),
      re_tax_bill_season: txt(dateText(row[CL.re_tax_bill_season], true)), re_fiscal_year: txt(row[CL.re_fiscal_year]), re_ratio: ratioOf(row[CL.re_ratio]),
      bpp_tax_bill_season: exempt ? null : txt(dateText(row[CL.bpp_tax_bill_season], true)), bpp_fiscal_year: exempt ? null : txt(row[CL.bpp_fiscal_year]), bpp_ratio: exempt ? null : ratioOf(row[CL.bpp_ratio]),
      re_exempt: false, pp_exempt: exempt,             // is_cook left inert (rule 6); Installments/Adjustment columns derived, not stored
    };

    const re = parseBlocks(row, CL.re_blocks, 'RE', valueYear);
    const pp = exempt ? { out: [], drops: [] } : parseBlocks(row, CL.bpp_blocks, 'PP', valueYear);   // rule 4: exempt → zero PP
    const installments = [...re.out, ...pp.out];
    [...re.drops, ...pp.drops].forEach(d => failures.push({ rowNo, drop: true, reason: `${collectorName}: ${d.kind} ${d.bill_type} — unparseable due date "${d.raw}" → DROPPED` }));
    if (!installments.length) failures.push({ rowNo, soft: true, reason: `${collectorName}: no valid installment blocks` });
    parsed.push({ state, assessorName, assessorFields, collectorName, collectorFields, installments, valueYear });
  });
  return { aByKey, parsed, failures };
}
